// useDictation — le micro du composeur.
//
// ── CE QU'IL EST, ET SURTOUT CE QU'IL N'EST PAS ─────────────────────────────
//
// Il ne contient AUCUNE règle. Tout ce qui se décide — assembler ce qui a été dit, n'envoyer
// que l'ajout, choisir la langue, juger si la voix peut rester sur la machine, quoi dire
// quand ça rate — vit dans src/lib/dictation.ts, en fonctions pures, éprouvées par 29 tests
// et 17 mutations. Ici il n'y a que ce qu'on ne peut pas éprouver sans navigateur : créer
// l'objet, brancher trois écouteurs, arrêter.
//
// C'est la posture de ce paquet : « platform-ui n'a pas de harnais de test de composants :
// tous ses tests portent sur des modules purs. Une règle écrite dans le composant ne serait
// protégée par rien. » (src/lib/chat_width.ts)
//
// ── LA VOIX RESTE SUR LA MACHINE QUAND C'EST POSSIBLE ───────────────────────
//
// MDN : sans `processLocally`, « on some browsers, like Chrome, … your audio is sent to a web
// service for recognition processing ». Nos utilisateurs paient pour construire leurs apps,
// pas pour que leur voix fasse un tour chez un tiers. On demande donc le local D'ABORD, et on
// ne retombe sur le service distant que si le navigateur ne sait pas faire autrement — sinon
// l'API échoue sur `language-not-supported` et la dictée ne marcherait simplement pas.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DICTATION_EMPTY,
  applyResult,
  dictationErrorNote,
  dictationLang,
  newFinal,
  releaseRecognition,
  shouldProcessLocally,
  speechRecognitionCtor,
  type Dictation,
  type ResultEventLike,
} from "../lib/dictation";

/** Ce que le navigateur offre, réduit à ce qu'on en utilise. Déclaré ici parce que
 *  `lib.dom` ne porte pas encore cette API dans la version de TypeScript du paquet. */
type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  processLocally?: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: ResultEventLike) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
};

type Disponibilite = (o: { langs: string[]; processLocally: boolean }) => Promise<string>;

export type DictationState = {
  /** Faux = ce navigateur ne sait pas, et le bouton ne doit PAS être rendu. Un micro qui ne
   *  fait rien est pire que pas de micro. */
  readonly supported: boolean;
  readonly listening: boolean;
  /** Ce qui est en train d'être dit, à montrer en gris. Jamais enregistré. */
  readonly interim: string;
  /** La raison, quand ça s'arrête mal. */
  readonly note: string | null;
  readonly toggle: () => void;
};

/**
 * @param onChunk reçoit chaque MORCEAU neuf de texte définitif, à ajouter à la boîte. Des
 *                morceaux, et pas le texte entier : réécrire la boîte effacerait ce que
 *                l'utilisateur tape pendant qu'il parle.
 */
export function useDictation(onChunk: (chunk: string) => void): DictationState {
  const Ctor = useMemo(
    () => (typeof window === "undefined" ? null : speechRecognitionCtor(window as never)),
    [],
  );
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [note, setNote] = useState<string | null>(null);

  const rec = useRef<Recognition | null>(null);
  /** Ce qui a déjà été posé dans la boîte. Une référence et pas un état : il est lu et écrit
   *  à l'intérieur d'un écouteur, qui ne doit pas dépendre d'un rendu pour être juste. */
  const envoye = useRef("");
  const dit = useRef<Dictation>(DICTATION_EMPTY);
  /** Le dernier appelant, pour que l'écouteur n'emprisonne pas une version périmée de
   *  `onChunk` (une fermeture sur un ancien `setText` écrirait dans le vide). */
  const chunk = useRef(onChunk);
  chunk.current = onChunk;

  const arreter = useCallback(() => {
    const r = rec.current;
    rec.current = null;
    setListening(false);
    setInterim("");
    releaseRecognition(r, "stop");
  }, []);

  // Une reconnaissance qui survit au démontage garderait le micro ouvert, et l'onglet
  // afficherait l'indicateur d'enregistrement sur une page que l'utilisateur a quittée.
  useEffect(() => () => {
    const r = rec.current;
    rec.current = null;
    releaseRecognition(r, "abort");
  }, []);

  const demarrer = useCallback(async () => {
    if (!Ctor || rec.current) return;
    setNote(null);
    envoye.current = "";
    dit.current = DICTATION_EMPTY;

    const r = new (Ctor as unknown as new () => Recognition)();
    const langue = dictationLang(typeof navigator === "undefined" ? {} : navigator);
    r.lang = langue;
    // `continuous` : on décrit une app en plusieurs phrases, pas en un mot.
    r.continuous = true;
    // `interimResults` : voir ses mots arriver est ce qui dit à l'utilisateur qu'on l'écoute.
    r.interimResults = true;

    // Le local d'abord — mais seulement si le navigateur l'annonce disponible. `available`
    // est statique sur le constructeur et n'existe pas partout, d'où la lecture prudente.
    const dispo = (Ctor as unknown as { available?: Disponibilite }).available;
    if (typeof dispo === "function") {
      try {
        r.processLocally = shouldProcessLocally(await dispo({ langs: [langue], processLocally: true }));
      } catch { r.processLocally = false; }
    }

    r.onresult = (e) => {
      dit.current = applyResult(dit.current, e);
      setInterim(dit.current.interim);
      const neuf = newFinal(envoye.current, dit.current.final);
      if (neuf !== "") {
        envoye.current = dit.current.final;
        chunk.current(neuf);
      }
    };
    r.onerror = (e) => {
      // `aborted` est ce que produit notre propre arrêt : le dire serait annoncer une panne
      // à quelqu'un qui vient de cliquer sur « stop ».
      if (e?.error !== "aborted") setNote(dictationErrorNote(String(e?.error ?? "")));
    };
    // Le navigateur arrête de lui-même après un silence, même en `continuous`. L'état doit
    // suivre, sinon le bouton reste allumé sur un micro fermé.
    r.onend = () => { if (rec.current === r) arreter(); };

    rec.current = r;
    setListening(true);
    try { r.start(); } catch { arreter(); setNote(dictationErrorNote("")); }
  }, [Ctor, arreter]);

  const toggle = useCallback(() => { if (rec.current) arreter(); else void demarrer(); }, [arreter, demarrer]);

  return { supported: Ctor !== null, listening, interim, note, toggle };
}
